import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Wallet, LogOut, ShieldCheck, ArrowRight, TrendingUp, History } from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useApp();
  const { t } = useLanguage();
  const [displayedAmount, setDisplayedAmount] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const targetAmount = currentUser.amount;
    const duration = 1500;
    const steps = 60;
    const increment = targetAmount / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetAmount) {
        setDisplayedAmount(targetAmount);
        clearInterval(timer);
      } else {
        setDisplayedAmount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [currentUser, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <header className="header-nav px-4 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-8 w-8" />
            <span className="font-bold text-[#0A2540] text-lg font-['Manrope'] tracking-tight">BMPay</span>
          </div>
          
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="hidden sm:block text-right">
              <p className="text-xs text-slate-500 uppercase tracking-wide">{t('account')}</p>
              <p className="text-sm font-medium text-[#0A2540]" data-testid="user-name">{currentUser.userName}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden sm:inline ml-2">{t('disconnect')}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-lg animate-slide-up">
          <div className="text-center mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-[#0A2540] font-['Manrope'] tracking-tight" data-testid="welcome-title">
              {t('welcome')} {currentUser.userName}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {t('manageGains')}
            </p>
          </div>

          <Card className="secure-card-navy rounded-xl overflow-hidden mb-5">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-[#0A2540] to-[#163454] px-5 py-6 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet className="h-4 w-4 text-white/70" />
                  <span className="text-xs font-medium text-white/70 uppercase tracking-wider">{t('availableBalance')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span 
                    className="text-3xl sm:text-4xl font-bold font-['Public_Sans'] tracking-tight animate-count-up"
                    data-testid="balance-amount"
                  >
                    {formatAmount(displayedAmount)}
                  </span>
                  <span className="text-lg font-semibold text-white/80" data-testid="balance-currency">
                    {currentUser.currency}
                  </span>
                </div>
              </div>

              <div className="px-5 py-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-900">{t('verifiedAccount')}</p>
                      <p className="text-xs text-slate-500">{t('readyForTransfer')}</p>
                    </div>
                  </div>
                  <div className="security-badge text-xs">
                    <ShieldCheck className="h-3 w-3" />
                    <span className="hidden sm:inline">{t('secured')}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Button
              onClick={() => navigate('/withdraw')}
              className="w-full h-12 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold text-sm rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl group"
              data-testid="withdraw-button"
            >
              <span>{t('requestTransfer')}</span>
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>

            <Button
              onClick={() => navigate('/user-history')}
              variant="outline"
              className="w-full h-11 border-slate-300 text-slate-700 hover:bg-slate-50 font-medium text-sm rounded-lg"
              data-testid="history-button"
            >
              <History className="mr-2 h-4 w-4" />
              {t('transferHistory')}
            </Button>
          </div>

          <p className="text-center text-xs text-slate-500 mt-5">
            {t('transferProcessed')}
          </p>
        </div>
      </main>

      <footer className="py-3 text-center border-t border-slate-200 bg-white/50">
        <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
          <ShieldCheck className="h-3 w-3 text-emerald-600" />
          <span>{t('securedByBMPay')}</span>
        </div>
      </footer>
    </div>
  );
};

export default DashboardPage;
